import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useDocuments } from "@/hooks/use-documents";
import { FileUpIcon, Loader2 } from "lucide-react";
import { useCurrentBusiness } from "@/hooks/use-businesses";

interface UploadDocumentDialogProps {
  transactionId?: number;
  buttonVariant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  children?: React.ReactNode;
}

export function UploadDocumentDialog({
  transactionId,
  buttonVariant = "outline",
  buttonSize = "default",
  children
}: UploadDocumentDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [open, setOpen] = useState(false);
  const { currentBusiness } = useCurrentBusiness();
  const { uploadDocument, isUploading } = useDocuments(currentBusiness?.id);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !currentBusiness) return;

    uploadDocument(
      { 
        file, 
        description: description || undefined, 
        transactionId: transactionId
      },
      {
        onSuccess: () => {
          setFile(null);
          setDescription("");
          setOpen(false);
        }
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant={buttonVariant} size={buttonSize}>
            <FileUpIcon className="mr-2 h-4 w-4" />
            Upload Document
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a document to your business records. Supported formats: PDF, JPEG, PNG, DOC, DOCX, CSV, XLS, XLSX.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="document-file">File</Label>
            <Input
              id="document-file"
              type="file"
              onChange={handleFileChange}
              required
              accept=".pdf,.jpeg,.jpg,.png,.doc,.docx,.csv,.xls,.xlsx"
            />
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="document-description">Description (Optional)</Label>
            <Textarea
              id="document-description"
              placeholder="Enter a description for this document"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={!file || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}